import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import { ArrowLeft, Trophy, CheckCircle, XCircle, Award, Download } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell } from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Results = () => {
  const navigate = useNavigate();
  const { resultId } = useParams();
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    loadResult();
  }, [resultId]);
  
  const loadResult = async () => {
    try {
      const response = await axios.get(`${API}/exam/result/${resultId}`, {
        withCredentials: true
      });
      setResult(response.data);
    } catch (error) {
      toast.error('Sonuç yüklenemedi');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-purple-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Sonuçlar yükleniyor...</p>
        </div>
      </div>
    );
  }
  
  if (!result) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>Sonuç bulunamadı</p>
      </div>
    );
  }
  
  // Handle both old format (score as percentage) and new format (correct_answers/total_questions)
  const totalQuestions = result.total_questions || 30;
  const correctAnswers = result.correct_answers || result.score || 0;
  const percentage = result.score > 100 ? result.score : (result.score || ((correctAnswers / totalQuestions) * 100)).toFixed(1);
  const determinedLevel = result.determined_level || result.level || 'A1';
  
  // Handle both 'answers' and 'graded_answers' formats
  const answers = result.answers || result.graded_answers || [];
  
  const getLevelInfo = (level) => {
    const info = {
      'A1': { title: 'Başlangıç', desc: 'Temel seviye İngilizce bilginiz var', color: 'from-gray-400 to-gray-600', emoji: '🌱' },
      'A2': { title: 'Temel', desc: 'Günlük konuşmalarda kendinizi ifade edebilirsiniz', color: 'from-blue-400 to-blue-600', emoji: '📚' },
      'B1': { title: 'Orta', desc: 'İş ve okul ortamlarında İngilizce kullanabilirsiniz', color: 'from-green-400 to-green-600', emoji: '✅' },
      'B2': { title: 'Orta Üstü', desc: 'Karmaşık metinleri anlayabilir ve akıcı konuşabilirsiniz', color: 'from-yellow-400 to-yellow-600', emoji: '⭐' },
      'C1': { title: 'İleri', desc: 'Akademik ve profesyonel düzeyde dil kullanımı', color: 'from-orange-400 to-orange-600', emoji: '🏆' },
      'C2': { title: 'Üst Düzey', desc: 'Ana dili gibi akıcı ve kusursuz kullanım', color: 'from-red-400 to-red-600', emoji: '👑' }
    };
    return info[level] || info['A1'];
  };
  
  const levelInfo = getLevelInfo(determinedLevel);
  
  // Prepare chart data - handle both category_performance and category_stats
  const categoryPerformance = result.category_performance || result.category_stats || {};
  const categoryData = Object.entries(categoryPerformance).map(([category, data]) => ({
    name: category.charAt(0).toUpperCase() + category.slice(1),
    percentage: data.percentage || (data.total > 0 ? Math.round((data.correct / data.total) * 100) : 0),
    correct: data.correct || 0,
    total: data.total || 0
  }));
  
  const COLORS = ['#3b82f6', '#8b5cf6', '#10b981', '#f59e0b'];
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 py-8">
      <div className="container mx-auto px-4 max-w-4xl">
        <Button
          variant="outline"
          onClick={() => navigate('/dashboard')}
          className="mb-6"
          data-testid="back-to-dashboard-button"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Dashboard'a Dön
        </Button>
        
        <Card className="glass-effect border-none shadow-2xl mb-8 overflow-hidden animate-slide-up" data-testid="results-summary-card">
          <div className={`h-2 bg-gradient-to-r ${levelInfo.color}`}></div>
          <CardHeader className="text-center pb-4">
            <div className="mx-auto mb-4 text-6xl">{levelInfo.emoji}</div>
            <CardTitle className="text-4xl font-bold mb-2">
              Tebrikler!
            </CardTitle>
            <p className="text-gray-600">Sınavınız başarıyla tamamlandı</p>
          </CardHeader>
          <CardContent className="text-center">
            <div className={`inline-block bg-gradient-to-r ${levelInfo.color} text-white px-8 py-4 rounded-2xl mb-4 shadow-lg`}>
              <div className="text-5xl font-bold mb-2">{determinedLevel}</div>
              <div className="text-xl">{levelInfo.title}</div>
            </div>
            
            <p className="text-lg text-gray-700 mb-6 max-w-md mx-auto">
              {levelInfo.desc}
            </p>
            
            <div className="grid grid-cols-2 gap-4 max-w-md mx-auto mb-6">
              <Card>
                <CardContent className="p-4 text-center">
                  <div className="text-3xl font-bold text-blue-600">{correctAnswers}</div>
                  <div className="text-sm text-gray-600">Doğru Cevap</div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 text-center">
                  <div className="text-3xl font-bold text-purple-600">{typeof percentage === 'number' ? percentage.toFixed(1) : percentage}%</div>
                  <div className="text-sm text-gray-600">Başarı Oranı</div>
                </CardContent>
              </Card>
            </div>
          </CardContent>
        </Card>
        
        {/* Category Performance Chart */}
        {categoryData.length > 0 && (
          <Card className="glass-effect border-none shadow-xl mb-8" data-testid="category-performance-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Award className="w-6 h-6" />
                Kategori Bazlı Performans
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={categoryData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis label={{ value: 'Başarı (%)', angle: -90, position: 'insideLeft' }} />
                  <Tooltip 
                    content={({ active, payload }) => {
                      if (active && payload && payload.length) {
                        return (
                          <div className="bg-white p-3 shadow-lg rounded-lg border">
                            <p className="font-bold">{payload[0].payload.name}</p>
                            <p className="text-sm text-blue-600">
                              {payload[0].payload.correct}/{payload[0].payload.total} Doğru
                            </p>
                            <p className="text-sm font-bold">{payload[0].value}%</p>
                          </div>
                        );
                      }
                      return null;
                    }}
                  />
                  <Bar dataKey="percentage" radius={[8, 8, 0, 0]}>
                    {categoryData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
              
              <div className="grid grid-cols-2 gap-4 mt-4">
                {categoryData.map((cat, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <span className="font-medium">{cat.name}</span>
                    <div>
                      <span className="text-sm text-gray-600">{cat.correct}/{cat.total}</span>
                      <span className={`ml-2 font-bold ${cat.percentage >= 70 ? 'text-green-600' : cat.percentage >= 50 ? 'text-yellow-600' : 'text-red-600'}`}>
                        {cat.percentage}%
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
        
        {answers.length > 0 && (
          <Card className="glass-effect border-none shadow-xl mb-8" data-testid="answers-detail-card">
            <CardHeader>
              <CardTitle>Cevap Detayları</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {answers.map((answer, idx) => (
                  <div
                    key={idx}
                    data-testid={`answer-detail-${idx}`}
                    className={`p-4 rounded-lg flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 ${
                      answer.is_correct ? 'bg-green-50 border-2 border-green-200' : 'bg-red-50 border-2 border-red-200'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      {answer.is_correct ? (
                        <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0" />
                      ) : (
                        <XCircle className="w-6 h-6 text-red-600 flex-shrink-0" />
                      )}
                      <div>
                        <span className="font-medium">Soru {idx + 1}</span>
                        {answer.level && <span className="ml-2 text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">{answer.level}</span>}
                        {answer.category && <span className="ml-2 text-xs bg-purple-100 text-purple-800 px-2 py-1 rounded">{answer.category}</span>}
                      </div>
                    </div>
                    <div className="text-sm text-gray-600">
                      {answer.is_correct ? 'Doğru ✓' : `Doğru cevap: ${typeof answer.correct_answer === 'number' ? (answer.correct_answer + 1) + '. seçenek' : answer.correct_answer}`}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
        
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Button
            data-testid="retake-exam-button"
            onClick={() => navigate('/exam')}
            size="lg"
            className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
          >
            Yeni Sınav Başlat
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Results;
